import { SidebarContext } from "core/contexts/sidebar/sidebar";
import { useContext, useEffect, useState } from "react";
import { deleteFavorites, getFavorites } from "./homeService";
import { AuthContext } from "core/contexts/auth/auth";
import colors from "./colors";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import GridViewIcon from "@mui/icons-material/GridView";
import { useNavigate } from "react-router";
import { setActiveModel } from "modules/chat/store/conversationList";
import { useAppDispatch, useAppSelector } from "core/store/reduxStore";
import { setBreadCrumbs, setHeader } from "core/store/shared/header";
import { setFavoritesCount } from "core/store/shared/sideBar";
import { Toast as showToast } from "core/components/global/toast/toast";
import { FilteredItemList } from "./filteredItem";
import { TagAvailableNowCheckbox, TagAvailableNowLayoutButtons, TagButtonsContainer, TagContainerStyle } from "./home.styled";
import { useAppTheme } from "core/contexts/theme/theme";
import { Tag } from "./tag";
import { navigateToPath } from "core/utils/common";

function FavoriteModels() {
  // Get context values
  const { modelData } = useContext(SidebarContext);
  const { checkAMSToken } = useContext(AuthContext);

  // Define state variables
  const [favorites, setfavorites] = useState<any>([]);
  const [viewMode, setViewMode] = useState("grid");
  const [filteredResults, setFilteredResults] = useState([]);

  const headerState = useAppSelector((state) => state.header);

  // Initialize hooks
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { apptheme } = useAppTheme();
  
  // Function to find matching model nodes based on favorites
  function findMatchingItems(nodes, favorites, matches: any = []) {
    for (const node of nodes) {
      if (node.sidebarProps && favorites.includes(node.state)) {
        matches.push(node);
      }
      if (node.children) {
        findMatchingItems(node.children, favorites, matches);
      }
    }
    return matches;
  }

  // Function to extract model list from favorites data and update state
  const getTargetComponentsList = (data) => {
    const favorites = data.map((item) => item.targetName);
    let modelValues = findMatchingItems(modelData, favorites);
    setFilteredResults(modelValues);
    dispatch(setFavoritesCount(modelValues.length));
  };

  useEffect(() => {
    if (favorites && favorites.length > 0) {
      getTargetComponentsList(favorites);
    }
  }, [favorites])
  
  // Use effect to fetch favorites and set breadcrumbs on component mount
  useEffect(() => {
    async function fetchFavorites() {
      const token = await checkAMSToken();
      const data = await getFavorites(token);
      setfavorites(data);
    }
    fetchFavorites();
    dispatch(setBreadCrumbs([{displayText:"Favorites",path:""}]));
  }, []);

  // Function to find a specific model node and its context in the model data tree
  function findNodeAndContext(tree, targetDisplayText, parent = null) {
    if (tree?.sidebarProps?.displayText === targetDisplayText) {
      return {
        parentDisplayText:
          (parent && parent["sidebarProps"]["displayText"]) || null,
        childrenDisplayTexts: (tree.children || [])
          .map((child) => child.sidebarProps?.displayText)
          .filter(Boolean),
      };
    }

    if (tree.children) {
      for (const child of tree.children) {
        const result = findNodeAndContext(child, targetDisplayText, tree);
        if (result) return result;
      }
    }

    return null;
  }

  function getBreadcrumbAndPath(targetDisplayText) {
    const breadcrumb: any = [];
    let targetPath: any = null;
    let targetParameter: any = null;

    function findItem(items) {
      for (const item of items) {
        if (item.sidebarProps.displayText === targetDisplayText) {
          breadcrumb.push({displayText:item.sidebarProps.displayText,path:item.path});
          if ("children" in item) {
            // Its a directory not a root, it should not even be here
            targetPath = "dir";
          } else {
            targetPath = item.path;
            if ("parameter" in item) {
              targetParameter = item.parameter;
            }
          }
          return true;
        }
        if (item.children) {
          breadcrumb.push({displayText:item.sidebarProps.displayText,path:item.path});
          if (findItem(item.children)) {
            return true;
          } else {
            breadcrumb.pop();
          }
        }
      }
      return false;
    }

    findItem(modelData);
    return { breadcrumb, path: targetPath, parameter: targetParameter };
  }

  /**
   * This function updates the crumb in the header, navigates to the path,
   * and sets the chat model if it is a chat object
   * @param displayText 
   */
  const handleChatNavigation = (displayText) => {
    const crumb: any = getBreadcrumbAndPath(displayText);
    console.log("2 breadcrumb", crumb);

    if (crumb.path !== "" && crumb.path !== "dir") {
      dispatch(setBreadCrumbs(crumb.breadcrumb));
    }

    navigateToPath(navigate, crumb, handleSetActiveModel);
  };

  /**
   * A required helper function to pass the component based dispatch and function to the helper hook
   * @param model: the AI model used for the selected model route
   */
  const handleSetActiveModel = (model) => {
    dispatch(setActiveModel(model));
  }

  /**
   * Function to update the favorites state by adding or removing a favorite by state ID
   * @param state: each sidebarItem has a unique ID called state, this function updates the favorited states
   */
  const updateFavoritesState = async (state) => {
    const token = await checkAMSToken();
    
    // Guard against null or undefined favorites
    if (!favorites) {
      console.error("Favorites is null or undefined in updateFavoritesState");
      showToast("Unable to update favorites at this time. Please try again later.", "error");
      return false;
    }
    
    const existFavorite = favorites.find(
      (provider) => provider.targetName === state
    );

    // If the favorite can be found locally proceed
    if (existFavorite) {
      const response = await deleteFavorites(token, existFavorite.targetName);

      // Delete it from the ui if successful, otherwise nothing will happen but the 
      if (response === true) {
        const newList = favorites.filter((t) => t._id !== existFavorite._id);
        setfavorites(newList);
        dispatch(setFavoritesCount(newList.length));
        return true;
      }
    }
    return false;
  };
  
  // Render the component
  return favorites&&favorites.length > 0 ? (
    // Render favorite routes/chat components/other components
    <TagContainerStyle className="flex-1 overflow-y-auto p-6 bg-gray-200/10 border-slate-700 shadow-lg transition-all duration-300 ease-in-out block">
      <div className="flex justify-between items-center mb-6">
        <div className="item-options flex items-center space-x-3">
          <TagAvailableNowLayoutButtons viewmode={viewMode} className="rounded-md shadow-sm p-1 flex">
            <button
              className="p-1.5 rounded"
              data-active={viewMode === "grid"}
              onClick={() => setViewMode("grid")}
            >
              <GridViewIcon sx={{ fontSize: 18, color: `${viewMode === "grid" ? apptheme.colors.white : apptheme.colors.textColor} !important` }} />
            </button>
            <button
              className="p-1.5 rounded"
              data-active={viewMode === "list"}
              onClick={() => setViewMode("list")}
            >
              <FormatListNumberedIcon sx={{ fontSize: 18, color: `${viewMode === "list" ? apptheme.colors.white : apptheme.colors.textColor} !important` }} />
            </button>
          </TagAvailableNowLayoutButtons>
        </div>
      </div>
      
      {/* Render models in grid view */}
      {viewMode === "grid" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredResults.map((filteredItem, index) => (
            <>
                <FilteredItemList
                  index={index}
                  filteredItem={filteredItem}
                  favorites={favorites}
                  handleChatNavigation={handleChatNavigation}
                  updateFavoritesState={updateFavoritesState}
                  handleSetActiveModel={handleSetActiveModel}
                  type={"grid"}
                />
            </>
          ))}
        </div>
      )}

      {viewMode === "list" && (
        <div className="space-y-3">
          {filteredResults.map((filteredItem, index) => (
            <FilteredItemList
              index={index}
              filteredItem={filteredItem}
              favorites={favorites}
              handleChatNavigation={handleChatNavigation}
              updateFavoritesState={updateFavoritesState}
              handleSetActiveModel={handleSetActiveModel}
              type={"list"}
            />
            ))}
          </div>
        )}
      </TagContainerStyle>
  ) : (
    // Render empty state if there are no favorites
    <></>
    // Placeholder for empty state message
  );
}

export default FavoriteModels;